import { getCurrentUserId } from '../utils/currentUser';
/**
 * userRole.js
 * --------------------------------
 * Canonical role helper for the frontend. Reads the runtime role from
 * `localStorage[KEYMADE_USER_ROLE_KEY]`. Two legacy keys
 * (`keyMadeTestRole`, `userRole`) are auto-migrated on first read so
 * existing browser sessions don't lose state.
 *
 * Pre-auth, falls back to 'principal_designer'. Replace the localStorage
 * lookup with a real auth-context reader when multi-tenant auth lands.
 *
 * Components should prefer the `useRole()` hook from `contexts/RoleContext`
 * for reactivity. The functions here are the imperative source of truth
 * the Provider hooks into.
 *
 * `setCurrentUserRole` writes the canonical key AND dispatches a
 * 'keyMadeRoleChanged' window event so the Provider can re-render without
 * a page reload.
 */

// Roles considered admin-level. Broader of the two scattered lists.
export const ADMIN_ROLES = ['admin', 'principal_designer', 'owner', 'assistant_designer'];

// Roles with financial-page access. Promoted from FinancialsPage / UserDashboard.
export const FINANCIAL_ROLES = [
  'admin', 'principal_designer', 'owner', 'assistant_designer', 'accountant',
];

// All known role names. Order does NOT imply hierarchy.
export const ALL_ROLES = [
  'admin',
  'principal_designer',
  'owner',
  'assistant_designer',
  'accountant',
  'designer',
  'contractor',
  'client',
  'team_member',
];

// Canonical localStorage key. Use only this key going forward.
export const KEYMADE_USER_ROLE_KEY = 'keyMadeUserRole';

// Legacy keys to migrate from. Lazy migration on first read.
const LEGACY_KEYS = ['keyMadeTestRole', 'userRole'];
const DEFAULT_ROLE = 'principal_designer';

const safeStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (_e) { /* private mode / SSR */ }
  return null;
};

/**
 * Read the current user's role. Lazy-migrates from legacy localStorage keys.
 */
export function getCurrentUserRole() {
  const storage = safeStorage();
  if (!storage) return DEFAULT_ROLE;
  const canonical = storage.getItem(KEYMADE_USER_ROLE_KEY);
  if (canonical) return canonical;
  for (const legacyKey of LEGACY_KEYS) {
    const legacyValue = storage.getItem(legacyKey);
    if (legacyValue) {
      try {
        storage.setItem(KEYMADE_USER_ROLE_KEY, legacyValue);
        storage.removeItem(legacyKey);
      } catch (_e) { /* quota / disabled */ }
      return legacyValue;
    }
  }
  return DEFAULT_ROLE;
}

/**
 * Persist a new role. Dispatches 'keyMadeRoleChanged' for in-tab reactivity;
 * cross-tab reactivity rides on the native 'storage' event.
 */
export function setCurrentUserRole(newRole) {
  const storage = safeStorage();
  if (storage) {
    try { storage.setItem(KEYMADE_USER_ROLE_KEY, newRole); } catch (_e) { /* ignore */ }
  }
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('keyMadeRoleChanged', { detail: { role: newRole } }));
    } catch (_e) { /* CustomEvent unsupported */ }
  }
}

/**
 * Current user object. Computed each call — not a static const.
 */
export function getCurrentUser() {
  return { id: getCurrentUserId(), role: getCurrentUserRole() };
}

export function isAdminRole(role) {
  return !!role && ADMIN_ROLES.includes(role);
}

export function isCurrentUserAdmin() {
  return isAdminRole(getCurrentUserRole());
}

export function hasFinancialAccess(role) {
  return !!role && FINANCIAL_ROLES.includes(role);
}

export function isCurrentUserFinancial() {
  return hasFinancialAccess(getCurrentUserRole());
}
